import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Seo } from '../components/Seo';
import { productSchema, breadcrumb, faqPageSchema } from '../lib/structuredData';
import { ogImage } from '../lib/structuredData';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useApp } from '../context/AppContext';
import {
  ChevronLeft, Star, Clock, MapPin, Users, Languages, Bus, ExternalLink, Check, X,
  BadgeCheck, CalendarDays, Info, ShieldCheck,
} from 'lucide-react';

const MATCH_STYLE = {
  EXACT_MATCH: 'bg-emerald-500/12 text-emerald-700 border-emerald-500/30',
  HIGHLY_COMPARABLE: 'bg-sky-500/12 text-sky-700 border-sky-500/30',
  PARTIALLY_COMPARABLE: 'bg-amber-500/12 text-amber-700 border-amber-500/30',
  NOT_COMPARABLE: 'bg-slate-500/12 text-slate-600 border-slate-500/30',
};
const CANCEL_LABEL = { free: 'Free cancellation', partial: 'Partial cancellation', none: 'Non-refundable', standard: 'See partner for terms' };

export default function TourDetail({ slug }) {
  const params = useParams();
  const tslug = slug || params.slug;
  const { currency } = useApp();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setT(null); setNotFound(false);
    api.tourDetail(tslug).then(({ data }) => { setT(data); })
      .catch(() => setNotFound(true));
    api.toursTrack('tour_view', { slug: tslug });
  }, [tslug]);

  const openOffer = (o) => {
    api.toursTrack('tour_provider_click', { slug: tslug, provider: o.provider_id });
    window.open(api.goVerticalUrl('tour', o.id, { surface: 'tour_detail', source: `/tours/${tslug}` }), '_blank');
  };

  if (notFound) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><p className="text-muted-foreground">Tour not found.</p><Link to="/tours" className="text-accent font-medium">Browse all tours</Link></div></Layout>;
  if (!t) return <Layout><div className="max-w-6xl mx-auto px-4 py-10"><Skeleton className="h-72 w-full rounded-2xl mb-4" /><Skeleton className="h-8 w-2/3 mb-2" /><Skeleton className="h-40 w-full rounded-2xl" /></div></Layout>;

  const best = t.best_offer;
  const offerPrices = (t.offers || []).map((o) => o.price).filter((p) => p != null);
  const lowPrice = best?.price ?? (offerPrices.length ? Math.min(...offerPrices) : null);
  const tourDesc = (
    `${t.duration_days}D/${t.duration_nights}N ${t.title}` +
    (t.cities?.length ? ` across ${t.cities.join(', ')}.` : '.') +
    (t.highlights?.length ? ` Highlights: ${t.highlights.slice(0, 3).join('; ')}.` : '') +
    ` Compare ${t.provider_count || 'multiple'} partners on FlightDeals.in.`
  ).slice(0, 300);
  const priceStr = lowPrice != null ? formatPrice(lowPrice, currency) : null;
  const tourFaqs = [
    {
      q: `How long is the ${t.title}?`,
      a: `This tour runs ${t.duration_days} days / ${t.duration_nights} nights${t.cities?.length ? `, covering ${t.cities.join(', ')}` : ''}.`,
    },
    {
      q: `How much does the ${t.title} cost?`,
      a: priceStr
        ? `Prices start from ${priceStr} per person. We compare ${t.provider_count || 'multiple'} partners on FlightDeals.in so you can pick the best value.`
        : `We compare ${t.provider_count || 'multiple'} partners on FlightDeals.in so you can pick the best value for this tour.`,
    },
    {
      q: `What's included in this tour?`,
      a: `${t.highlights?.length ? `Highlights include ${t.highlights.slice(0, 3).join(', ')}. ` : ''}Inclusions such as accommodation, guided activities and transport vary by partner — compare them side by side before booking.`,
    },
    {
      q: `Can I cancel or get a refund?`,
      a: `Cancellation policies differ by partner and some offer free cancellation. Check each provider's policy in the "Compare providers" panel before you book.`,
    },
  ];
  if (t.language) {
    tourFaqs.push({
      q: `What language is the ${t.title} conducted in?`,
      a: `This tour is typically conducted in ${t.language}.`,
    });
  }
  const tourJsonLd = [
    productSchema({
      name: t.title,
      description: tourDesc,
      image: t.images?.[0],
      path: `/tours/${tslug}`,
      price: lowPrice,
      offerCount: t.offers?.length,
      rating: t.rating,
      reviewCount: t.review_count,
    }),
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Tours', path: '/tours' },
      { name: (t.destination_slug || '').replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()), path: `/tours/${t.destination_slug}` },
      { name: t.title },
    ]),
    faqPageSchema(tourFaqs),
  ];
  return (
    <Layout>
      <Seo title={t.title} description={tourDesc} path={`/tours/${tslug}`}
        image={ogImage(`tour/${tslug}.png`)} type="product" jsonLd={tourJsonLd} />
      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground">
        {t.images?.[0] && <img src={t.images[0]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Link to={t.destination_slug ? `/tours/${t.destination_slug}` : '/tours'} className="inline-flex items-center gap-1 text-sm text-primary-foreground/80 hover:text-primary-foreground mb-2" data-testid="tour-back"><ChevronLeft className="h-4 w-4" /> {t.destination_slug ? `Tours in ${t.destination_slug}` : 'All tours'}</Link>
          <div className="flex flex-wrap gap-2 mb-2">
            {(t.categories || []).slice(0, 4).map((c) => <Badge key={c} variant="outline" className="rounded-full bg-white/10 border-white/20 text-primary-foreground text-[10px]">{c}</Badge>)}
            {t.is_demo && <Badge variant="outline" className="rounded-full bg-amber-500/20 border-amber-300/40 text-amber-100 text-[10px]">DEMO</Badge>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold max-w-3xl" data-testid="tour-title">{t.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-primary-foreground/90">
            {t.cities?.length > 0 && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4 text-accent" /> {t.cities?.join(' → ')}</span>}
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4 text-accent" /> {t.duration_days}D / {t.duration_nights}N</span>
            {t.rating && <span className="inline-flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {t.rating} ({t.review_count})</span>}
            {t.group_size && <span className="inline-flex items-center gap-1"><Users className="h-4 w-4 text-accent" /> {t.group_size}</span>}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          {/* About */}
          {t.description && (
            <Card className="p-5 rounded-2xl" data-testid="tour-about">
              <h2 className="font-display text-lg font-semibold mb-2">About this tour</h2>
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{t.description}</p>
            </Card>
          )}

          {/* Photo gallery */}
          {t.images?.length > 1 && (
            <Card className="p-5 rounded-2xl" data-testid="tour-gallery">
              <h2 className="font-display text-lg font-semibold mb-3">Photos</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {t.images.slice(0, 6).map((img, i) => (
                  <img key={i} src={img} alt={`${t.title} photo ${i + 1}`} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-xl" />
                ))}
              </div>
            </Card>
          )}

          {/* Highlights */}
          {t.highlights?.length > 0 && (
            <Card className="p-5 rounded-2xl">
              <h2 className="font-display text-lg font-semibold mb-3">Highlights</h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {(t.highlights || []).map((h, i) => <div key={`hl-${i}`} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 mt-0.5 text-accent shrink-0" /> {h}</div>)}
              </div>
              {(t.transport || t.language || t.start_location) && (
                <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
                  {t.transport && <div className="rounded-xl border border-border p-3"><p className="text-xs text-muted-foreground flex items-center gap-1"><Bus className="h-3.5 w-3.5" /> Transport</p><p className="font-medium mt-0.5">{t.transport}</p></div>}
                  {t.language && <div className="rounded-xl border border-border p-3"><p className="text-xs text-muted-foreground flex items-center gap-1"><Languages className="h-3.5 w-3.5" /> Language</p><p className="font-medium mt-0.5">{t.language}</p></div>}
                  {t.start_location && <div className="rounded-xl border border-border p-3"><p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Start → End</p><p className="font-medium mt-0.5">{t.start_location} → {t.end_location}</p></div>}
                </div>
              )}
            </Card>
          )}

          {/* What's included / excluded */}
          {(t.inclusions?.length > 0 || t.exclusions?.length > 0) && (
            <Card className="p-5 rounded-2xl" data-testid="tour-inclusions">
              <h2 className="font-display text-lg font-semibold mb-3">What's included</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <ul className="space-y-1.5">
                  {(t.inclusions || []).map((inc, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> {inc}</li>)}
                </ul>
                {t.exclusions?.length > 0 && (
                  <ul className="space-y-1.5">
                    {(t.exclusions || []).map((exc, i) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><X className="h-4 w-4 mt-0.5 text-rose-500 shrink-0" /> {exc}</li>)}
                  </ul>
                )}
              </div>
            </Card>
          )}

          {/* Good to know */}
          {t.additional_info?.length > 0 && (
            <Card className="p-5 rounded-2xl" data-testid="tour-good-to-know">
              <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Info className="h-5 w-5 text-accent" /> Good to know</h2>
              <ul className="space-y-1.5">
                {(t.additional_info || []).map((a, i) => <li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/50 shrink-0" /> {a}</li>)}
              </ul>
            </Card>
          )}

          {/* Day-by-day itinerary (when the provider supplies one) */}
          {t.daily_itinerary?.length > 0 && (
            <Card className="p-5 rounded-2xl" data-testid="tour-itinerary">
              <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-accent" /> Day-by-day itinerary</h2>
              <ol className="relative border-l border-border ml-3">
                {(t.daily_itinerary || []).map((d) => (
                  <li key={d.day_number} className="mb-5 ml-5">
                    <span className="absolute -left-3 grid place-items-center h-6 w-6 rounded-full bg-accent text-accent-foreground text-[11px] font-semibold">{d.day_number}</span>
                    <p className="font-semibold text-sm">{d.title}</p>
                    <p className="text-sm text-muted-foreground">{d.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {(d.activities || []).map((a) => <Badge key={a} variant="outline" className="rounded-full text-[10px]">{a}</Badge>)}
                      {d.overnight_location && d.overnight_location !== '-' && <Badge variant="outline" className="rounded-full text-[10px] bg-secondary">Overnight: {d.overnight_location}</Badge>}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>

        {/* Compare providers */}
        <div className="lg:col-span-1">
          <Card className="p-5 rounded-2xl lg:sticky lg:top-24" data-testid="tour-compare">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-lg font-semibold">Compare providers</h2>
              <Badge variant="outline" className="rounded-full text-[10px]">{t.provider_count}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3 flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5" /> {t.comparison_note}</p>
            <div className="space-y-3">
              {(t.offers || []).map((o, i) => {
                const isBest = best && o.id === best.id;
                return (
                  <div key={o.id} className={`rounded-xl border p-3 ${isBest ? 'border-accent/40 bg-accent/5' : 'border-border'}`} data-testid={`tour-offer-${o.provider_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm">{o.provider_name}</p>
                      {isBest && <Badge className="rounded-full bg-accent text-accent-foreground text-[10px]"><BadgeCheck className="h-3 w-3 mr-0.5" /> Lowest price</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-0.5 text-amber-600"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />{o.rating}</span>
                      <span>· {CANCEL_LABEL[o.cancellation_policy]}</span>
                    </div>
                    <Badge variant="outline" className={`rounded-full text-[10px] mt-1.5 ${MATCH_STYLE[o.match_type] || ''}`}>{o.match_type.replace(/_/g, ' ').toLowerCase()} · {Math.round(o.match_confidence * 100)}%</Badge>
                    <ul className="mt-2 space-y-0.5">
                      {(o.inclusions || []).slice(0, 3).map((inc, idx) => <li key={`${o.id}-inc-${idx}`} className="text-[11px] text-muted-foreground flex items-start gap-1"><Check className="h-3 w-3 mt-0.5 text-accent shrink-0" />{inc}</li>)}
                    </ul>
                    <div className="mt-2 flex items-end justify-between">
                      <p className="text-lg font-semibold tabular">{formatPrice(o.price, currency)}<span className="text-[10px] text-muted-foreground font-normal">/person</span></p>
                      <Button onClick={() => openOffer(o)} className="h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid={`tour-book-${o.provider_id}`}>View <ExternalLink className="h-3.5 w-3.5 ml-1" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5" /> We never present different products as identical. Match confidence reflects how comparable each partner's tour is.</p>
            <div className="mt-3"><AffiliateDisclosure variant="footer" /></div>
          </Card>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="tour-faq">
        <h2 className="font-display text-lg font-semibold mb-4">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card divide-y divide-border">
          {tourFaqs.map((f, i) => (
            <AccordionItem key={i} value={`tfaq-${i}`} className="px-4 border-b-0">
              <AccordionTrigger className="text-left font-medium hover:no-underline" data-testid={`tour-faq-q-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground" data-testid={`tour-faq-a-${i}`}>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Layout>
  );
}
